<script setup lang="ts"></script>
<template>
  <div class="terms-container">
    <h1 class="text-2xl font-bold text-gray-900 mb-6">TERMS AND CONDITIONS</h1>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">1. ACCEPTANCE OF TERMS</h2>
      <p class="text-gray-700 mb-4">
        By accessing or using the travel reference application (hereinafter referred to as "the
        Application"), you acknowledge that you have read, understood, and agree to be bound by
        these Terms and Conditions. If you do not agree to these terms, please discontinue use of
        the Application immediately.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">
        2. APPLICATION PURPOSE AND LIMITATIONS
      </h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">2.1. Reference Only</h3>
        <p class="text-gray-700">
          The Application is provided solely as a reference tool for viewing travel-related
          information. It is not intended to serve as, nor should it be relied upon as, official
          travel documentation.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">2.2. Non-Authoritative Source</h3>
        <p class="text-gray-700">
          The Application is not the source of truth for any travel arrangements, bookings, or
          associated financial transactions. All definitive information regarding travel
          arrangements is contained exclusively in the official confirmations and documentation
          initially provided to you by your authorized travel agent or service provider.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">
          2.3. Data Synchronization Limitations
        </h3>
        <p class="text-gray-700">
          The Application interfaces with multiple external systems and databases, which may result
          in delays, inaccuracies, or inconsistencies in the information displayed. Data
          synchronization occurs periodically and not in real-time.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">3. DISCLAIMER OF WARRANTIES</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">3.1. "AS IS" Basis</h3>
        <p class="text-gray-700">
          The Application is provided on an "AS IS" and "AS AVAILABLE" basis. The Company makes no
          representations or warranties of any kind, express or implied, regarding the operation,
          reliability, or accuracy of the Application.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">3.2. No Guarantees</h3>
        <p class="text-gray-700 mb-2">
          The Company explicitly disclaims all warranties, including but not limited to:
        </p>
        <ul class="list-disc ml-8 text-gray-700">
          <li>Accuracy, completeness, or timeliness of information displayed</li>
          <li>Fitness for a particular purpose</li>
          <li>Non-infringement of third-party rights</li>
          <li>System compatibility or availability</li>
          <li>Security or freedom from errors, interruptions, or viruses</li>
        </ul>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">3.3. Third-Party Information</h3>
        <p class="text-gray-700">
          Some of the information displayed in the Application originates from third-party sources
          over which the Company has no control. The Company does not warrant or guarantee the
          accuracy of such information.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">4. LIMITATION OF LIABILITY</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.1. No Responsibility for Damages</h3>
        <p class="text-gray-700 mb-2">
          Under no circumstances shall the Company, its affiliates, officers, directors, employees,
          agents, or partners be liable for any direct, indirect, incidental, special,
          consequential, or punitive damages, including but not limited to:
        </p>
        <ul class="list-disc ml-8 text-gray-700">
          <li>Missed flights, connections, or other travel arrangements</li>
          <li>
            Additional costs incurred due to reliance on information displayed in the Application
          </li>
          <li>Financial losses of any kind</li>
          <li>Loss of opportunity, goodwill, or reputation</li>
          <li>Loss of data</li>
          <li>Business interruption</li>
          <li>Personal injury</li>
          <li>Any other losses or damages, regardless of the cause</li>
        </ul>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.2. Data Inconsistencies</h3>
        <p class="text-gray-700">
          The Company explicitly disclaims all liability for any damages, losses, or costs arising
          from or related to inconsistencies, inaccuracies, or discrepancies between the information
          displayed in the Application and the information contained in official travel
          documentation.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.3. Travel Disruptions</h3>
        <p class="text-gray-700">
          The Company shall not be liable for any costs, damages, or inconvenience resulting from
          travel disruptions, regardless of whether information regarding such disruptions was or
          should have been displayed in the Application.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.4. System Downtime</h3>
        <p class="text-gray-700">
          The Company shall not be liable for any damages resulting from temporary unavailability,
          malfunction, or failure of the Application.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.5. Cap on Liability</h3>
        <p class="text-gray-700">
          In jurisdictions where limitation of liability for certain damages is not permitted, the
          Company's liability shall be limited to the maximum extent permitted by applicable law. In
          all cases, the Company's total liability shall not exceed the amount paid by you (if any)
          for access to the Application in the 12 months preceding the claim.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">5. USER RESPONSIBILITIES</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.1. Verification Requirement</h3>
        <p class="text-gray-700">
          Users are required to verify all critical travel information (including but not limited to
          flight dates, times, locations, booking status, and payment information) against official
          documentation provided directly by the travel service provider or authorized agent.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.2. Timeliness</h3>
        <p class="text-gray-700">
          Users are solely responsible for ensuring they arrive at the appropriate locations at the
          times specified in official travel documentation, regardless of any information displayed
          in the Application.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.3. Official Documentation</h3>
        <p class="text-gray-700">
          Users must maintain and carry all required official travel documentation (including but
          not limited to boarding passes, tickets, and identification) as required by travel service
          providers, transportation authorities, and applicable laws.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.4. Reporting Discrepancies</h3>
        <p class="text-gray-700">
          Users should report any discrepancies between information displayed in the Application and
          official travel documentation to their authorized travel agent immediately upon discovery.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">
        6. SYSTEM AVAILABILITY AND MODIFICATION
      </h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">6.1. No Availability Guarantee</h3>
        <p class="text-gray-700">
          The Company does not guarantee the Application will be available at all times or that
          access will be uninterrupted or error-free.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">6.2. Modification Rights</h3>
        <p class="text-gray-700">
          The Company reserves the right to modify, suspend, or discontinue the Application or any
          part thereof at any time without notice or liability.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">6.3. Maintenance</h3>
        <p class="text-gray-700">
          The Application may be temporarily unavailable due to maintenance, updates, or technical
          issues without prior notice.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">7. DATA PROTECTION AND PRIVACY</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">7.1. Data Processing</h3>
        <p class="text-gray-700">
          Use of the Application involves the processing of personal data as described in the
          Company's Privacy Policy, which is incorporated by reference into these Terms and
          Conditions.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">7.2. No Guarantee of Security</h3>
        <p class="text-gray-700">
          While the Company implements reasonable security measures, it cannot guarantee that
          unauthorized third parties will never be able to defeat those measures or use personal
          information for improper purposes.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">8. INTELLECTUAL PROPERTY</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">8.1. Ownership</h3>
        <p class="text-gray-700">
          The Application, including all content, features, and functionality, is owned by the
          Company and is protected by intellectual property laws.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">8.2. Limited License</h3>
        <p class="text-gray-700">
          Users are granted a limited, non-exclusive, non-transferable license to access and use the
          Application for their personal, non-commercial use only.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">9. GOVERNING LAW AND JURISDICTION</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">9.1. Governing Law</h3>
        <p class="text-gray-700">
          These Terms and Conditions shall be governed by and construed in accordance with the laws
          of [Jurisdiction], without regard to its conflict of law provisions.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">9.2. Dispute Resolution</h3>
        <p class="text-gray-700">
          Any dispute arising out of or relating to these Terms shall be resolved exclusively
          through the courts of [Jurisdiction].
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">10. MISCELLANEOUS PROVISIONS</h2>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.1. Severability</h3>
        <p class="text-gray-700">
          If any provision of these Terms is found to be unenforceable or invalid, that provision
          shall be limited or eliminated to the minimum extent necessary so that these Terms shall
          otherwise remain in full force and effect.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.2. No Waiver</h3>
        <p class="text-gray-700">
          No waiver of any term of these Terms shall be deemed a further or continuing waiver of
          such term or any other term.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.3. Assignment</h3>
        <p class="text-gray-700">
          The Company may assign its rights and obligations under these Terms without restriction.
          Users may not assign their rights or obligations without prior written consent from the
          Company.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.4. Entire Agreement</h3>
        <p class="text-gray-700">
          These Terms constitute the entire agreement between users and the Company concerning the
          subject matter hereof.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.5. Amendments</h3>
        <p class="text-gray-700">
          The Company reserves the right to modify these Terms at any time without prior notice.
          Continued use of the Application following any such changes shall constitute your consent
          to such changes.
        </p>
      </div>
      <div class="mb-3">
        <h3 class="text-lg font-medium text-gray-800 mb-2">10.6. Force Majeure</h3>
        <p class="text-gray-700">
          The Company shall not be liable for any failure to perform its obligations hereunder where
          such failure results from any cause beyond the Company's reasonable control.
        </p>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">11. CONTACT INFORMATION</h2>
      <p class="text-gray-700 mb-4">
        If you have any questions about these Terms and Conditions, please contact your authorized
        travel agent directly. Issues specifically regarding Application functionality (not travel
        arrangements) may be submitted via the feedback mechanism provided within the Application.
      </p>
    </section>

    <div class="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <p class="text-gray-800 font-medium text-center">
        BY USING THE APPLICATION, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS AND CONDITIONS,
        UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO ALL OF THE TERMS AND
        CONDITIONS, YOU MUST NOT ACCESS OR USE THE APPLICATION.
      </p>
      <p class="text-gray-600 text-sm text-center mt-3">Last updated: April 15th, 2025</p>
    </div>
  </div>

  <div class="privacy-policy-container">
    <h1 class="text-2xl font-bold text-gray-900 mb-6">PRIVACY POLICY</h1>
    <p class="text-gray-600 mb-6"><strong>Last Updated: April 15th, 2025</strong></p>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">1. INTRODUCTION</h2>
      <p class="text-gray-700 mb-4">
        Welcome to our Travel Reference Application (the "Application"). We respect your privacy and
        are committed to protecting your personal data. This Privacy Policy explains how we collect,
        use, disclose, and safeguard your information when you use our Application.
      </p>
      <p class="text-gray-700 mb-4">
        Please read this Privacy Policy carefully. If you do not agree with the terms of this
        Privacy Policy, please do not access the Application.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">2. DATA CONTROLLER</h2>
      <p class="text-gray-700 mb-4">
        For the purposes of applicable data protection laws, BWC NY Management INC. (the "Company,"
        "we," "us," or "our") is the data controller responsible for the personal information
        collected through the Application.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">3. INFORMATION WE COLLECT</h2>
      <p class="text-gray-700 mb-4">
        We may collect several types of information from and about users of our Application,
        including:
      </p>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">3.1 Personal Data</h3>
        <p class="text-gray-700 mb-2">
          Information that personally identifies you or those that you represent, such as:
        </p>
        <ul class="list-disc ml-6 text-gray-700">
          <li>Full name</li>
          <li>Contact information (email address, phone number)</li>
          <li>Travel preferences</li>
          <li>Travel history</li>
          <li>Payment information (stored securely through third-party encrypted vaults)</li>
          <li>Government-issued identification information necessary for travel arrangements</li>
          <li>User credentials for the Application</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">3.2 Non-Personal Data</h3>
        <p class="text-gray-700 mb-2">
          Information about your use of the Application that does not personally identify you, such
          as:
        </p>
        <ul class="list-disc ml-6 text-gray-700">
          <li>Usage data and analytics</li>
          <li>Device information (device type, system, browser type)</li>
          <li>IP address</li>
          <li>Application performance data</li>
          <li>User interaction metrics</li>
        </ul>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">4. HOW WE COLLECT YOUR INFORMATION</h2>
      <p class="text-gray-700 mb-4">We collect information:</p>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.1 Directly From You</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>When you register for an account</li>
          <li>When you provide travel information to your authorized travel agent</li>
          <li>When you input preferences or update your profile</li>
          <li>When you make a transaction with your authorized travel agent</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">4.2 Automatically</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>Through server logs, usage and telemetry analytics</li>
          <li>Through your device or browser when you access the Application</li>
        </ul>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">5. HOW WE USE YOUR INFORMATION</h2>
      <p class="text-gray-700 mb-4">
        We use the information we collect for the following purposes:
      </p>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">
          5.1 Providing and Improving the Application
        </h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>To display your travel information in the Application</li>
          <li>To maintain and improve the functionality of the Application</li>
          <li>To develop new features and services</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.2 Communication</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>To respond to your inquiries and support requests</li>
          <li>To send important notices regarding your travel arrangements</li>
          <li>To provide you with updates about the Application</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.3 Security and Compliance</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>To protect the security and integrity of the Application</li>
          <li>To detect and prevent fraud or unauthorized access</li>
          <li>To comply with legal obligations and industry standards</li>
          <li>To enforce our Terms and Conditions</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">5.4 Analytics and Research</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>To analyze usage patterns and trends</li>
          <li>To measure the effectiveness of our Application</li>
          <li>To conduct research to improve our services</li>
        </ul>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">6. LEGAL BASIS FOR PROCESSING</h2>
      <p class="text-gray-700 mb-4">
        We process your personal data based on one or more of the following legal bases:
      </p>
      <ul class="list-disc ml-6 text-gray-700">
        <li>Performance of a contract with you</li>
        <li>Compliance with a legal obligation</li>
        <li>Protection of your vital interests or those of another person</li>
        <li>
          Our legitimate interests, provided these do not override your fundamental rights and
          freedoms
        </li>
        <li>Your consent, where applicable</li>
      </ul>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">7. DATA SHARING AND DISCLOSURE</h2>
      <p class="text-gray-700 mb-4">We may share your information with:</p>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">7.1 Legal Requirements</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>In response to lawful requests by public authorities</li>
          <li>To comply with legal obligations</li>
          <li>To protect our rights, privacy, safety, or property</li>
          <li>To detect, prevent, or address fraud or security issues</li>
        </ul>
      </div>

      <div class="mb-4">
        <h3 class="text-lg font-medium text-gray-800 mb-2">7.2 Vendors</h3>
        <ul class="list-disc ml-6 text-gray-700">
          <li>
            To provide third-party vendors with information required to book and provide their
            services to you
          </li>
        </ul>
      </div>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">8. DATA RETENTION</h2>
      <p class="text-gray-700 mb-4">
        We will retain your personal data only for as long as necessary to fulfill the purposes for
        which it was collected, including for the purposes of satisfying any legal, regulatory, tax,
        accounting, or reporting requirements.
      </p>
      <p class="text-gray-700 mb-4">
        When determining the appropriate retention period, we consider:
      </p>
      <ul class="list-disc ml-6 text-gray-700">
        <li>The amount, nature, and sensitivity of the personal data</li>
        <li>The potential risk of harm from unauthorized use or disclosure</li>
        <li>The purposes for which we process the data</li>
        <li>Whether we can achieve those purposes through other means</li>
        <li>Applicable legal, regulatory, tax, accounting, or other requirements</li>
      </ul>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">9. DATA SECURITY</h2>
      <p class="text-gray-700 mb-4">
        We have implemented appropriate technical and organizational measures to secure your
        personal data from accidental loss, unauthorized access, alteration, or disclosure. However,
        no method of transmission over the Internet or electronic storage is completely secure, and
        we cannot guarantee absolute security.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">10. YOUR DATA PROTECTION RIGHTS</h2>
      <p class="text-gray-700 mb-4">
        Depending on your location, you may have the following rights regarding your personal data:
      </p>
      <ul class="list-disc ml-6 text-gray-700">
        <li><strong>Right to Access</strong>: Request access to your personal data</li>
        <li>
          <strong>Right to Rectification</strong>: Request correction of inaccurate personal data
        </li>
        <li><strong>Right to Erasure</strong>: Request deletion of your personal data</li>
        <li>
          <strong>Right to Restrict Processing</strong>: Request restriction of processing of your
          personal data
        </li>
        <li><strong>Right to Data Portability</strong>: Request transfer of your personal data</li>
        <li><strong>Right to Object</strong>: Object to processing of your personal data</li>
        <li><strong>Right to Withdraw Consent</strong>: Withdraw your consent at any time</li>
      </ul>
      <p class="text-gray-700 mt-4">
        To exercise any of these rights, please contact us using the details provided in the
        "Contact Us" section.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">11. THIRD-PARTY LINKS</h2>
      <p class="text-gray-700 mb-4">
        The Application may contain links to third-party websites or services. We are not
        responsible for the privacy practices or content of these third parties. We encourage you to
        review the privacy policies of any third-party websites or services you visit.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">
        12. COOKIES AND TRACKING TECHNOLOGIES
      </h2>
      <p class="text-gray-700 mb-4">
        We use cookies to authenticate users, remember user preferences, and analyze how users
        interact to:
      </p>
      <ul class="list-disc ml-6 text-gray-700">
        <li>Keep you signed in</li>
        <li>Serve and manage our Application</li>
        <li>Understand how you use our Application</li>
      </ul>
      <p class="text-gray-700 mt-4">
        You can control cookies through your browser settings and other tools. However, if you block
        certain cookies, you may not be able to use all the features of the Application.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">13. CHANGES TO THIS PRIVACY POLICY</h2>
      <p class="text-gray-700 mb-4">
        We may update this Privacy Policy from time to time to reflect changes in our practices or
        for other operational, legal, or regulatory reasons. The updated version will be indicated
        by an updated "Last Updated" date. We encourage you to review this Privacy Policy frequently
        to stay informed about how we are protecting your information.
      </p>
    </section>

    <section class="mb-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-3">14. CONTACT US</h2>
      <p class="text-gray-700 mb-4">
        If you have any questions, concerns, or requests regarding this Privacy Policy or our
        privacy practices, please contact us at ge@globalelite.us
      </p>
    </section>
    <div class="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <p class="text-gray-800 font-medium text-center">
        By using the Application, you acknowledge that you have read and understood this Privacy
        Policy and agree to the collection, use, and disclosure of your information as described
        herein.
      </p>
    </div>
  </div>
</template>
